import { useRouter } from 'next/router';
import React, { memo, useEffect, useRef } from 'react';

import Navigation from 'components/Navigation/Navigation';
import { useCopyStore, useGlobalStore } from 'store';
import { ReactComponent as BackIcon } from 'svgs/backCircle.svg';
import gtm, { actions, categories } from 'u9/utils/gtm';
import { ROUTES } from 'utils/routes';

import * as Styled from './Header.styles';

export interface HeaderProps {}

const defaultProps: Partial<HeaderProps> = {};

const Header: React.FC<HeaderProps> = () => {
  const {
    currentTheme,
    currentChapter,
    setCurrentTheme,
    isCardExpanded,
    isLandingVisible,
    isSmallDevice,
    setLandingVisible,
    isSidebarExpanded,
  } = useGlobalStore();
  const {
    copy: { global, themes, accessibility },
  } = useCopyStore();
  const router = useRouter();
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (titleRef?.current && !isSmallDevice) {
      setTimeout(() => {
        titleRef?.current?.focus();
      }, 600);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleRef, currentChapter]);

  const handleBack = () => {
    setCurrentTheme(null);
    setTimeout(
      () => {
        router.push(ROUTES.HOME);
      },
      isCardExpanded ? 600 : 0
    );
    gtm.trackEvent(
      categories[`${router.query.id}`],
      actions.click,
      global?.common?.allThemesBtn
    );
  };

  const backToLanding = () => {
    setCurrentTheme(null);
    setTimeout(
      () => {
        router.push(ROUTES.HOME);
        setLandingVisible(true);
      },
      isCardExpanded ? 600 : 0
    );
  };

  return (
    <Styled.Container
      initial={{ opacity: 0, y: '-100%' }}
      animate={
        currentTheme ? { opacity: 1, y: '0%' } : { opacity: 0, y: '-100%' }
      }
      transition={{ duration: 0.6 }}
      aria-hidden={
        router.pathname === ROUTES.HOME || isLandingVisible || isSidebarExpanded
      }
    >
      <Styled.Wrapper>
        <Styled.Label
          dangerouslySetInnerHTML={{ __html: global?.common?.mapLabel }}
          aria-label={accessibility?.map?.title}
          onClick={backToLanding}
        />
        <Styled.BackButtonWrapper
          onClick={handleBack}
          aria-hidden={isLandingVisible || isSidebarExpanded}
          aria-label={accessibility?.shared?.allThemesBtn}
        >
          <BackIcon />
        </Styled.BackButtonWrapper>
        {themes?.[currentTheme]?.title && (
          <Styled.Title
            tabIndex={0}
            initial={{ opacity: 0 }}
            animate={currentTheme ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6 }}
            dangerouslySetInnerHTML={{ __html: themes?.[currentTheme]?.title }}
            ref={titleRef}
            aria-hidden={isLandingVisible || isSidebarExpanded}
          />
        )}
      </Styled.Wrapper>
      {!isSmallDevice && (
        <Navigation
          items={themes?.[currentTheme]?.chapters}
          backButtonText={global?.common?.allThemesBtn}
          accessibilitBackButtonText={accessibility?.shared?.allThemesBtn}
          handleBack={handleBack}
        />
      )}
    </Styled.Container>
  );
};

Header.defaultProps = defaultProps;

export default memo(Header);
